"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export interface SegmentingExamplesGalleryItem {
  src: string;
  alt: string;
  label?: string;
  width?: number;
  height?: number;
}

interface SegmentingExamplesGalleryProps {
  className?: string;
  items: SegmentingExamplesGalleryItem[];
}

const layoutClasses = [
  "h-80 md:col-span-2",
  "h-80 md:col-span-3",
  "h-96 md:col-span-5",
  "h-80 md:col-span-2",
  "h-80 md:col-span-3",
  "h-80 md:col-span-3",
  "h-80 md:col-span-2",
  "h-96 md:col-span-5",
  "h-80 md:col-span-2",
  "h-80 md:col-span-3",
] as const;

const SegmentingExamplesGallery = ({
  className,
  items,
}: SegmentingExamplesGalleryProps) => {
  const [selectedItem, setSelectedItem] =
    React.useState<SegmentingExamplesGalleryItem | null>(null);
  const [dimensionsBySrc, setDimensionsBySrc] = React.useState<
    Record<string, { width: number; height: number }>
  >({});

  if (items.length === 0) {
    return null;
  }

  const selectedAspectRatio = selectedItem
    ? (selectedItem.width ?? dimensionsBySrc[selectedItem.src]?.width ?? 1600) /
      (selectedItem.height ?? dimensionsBySrc[selectedItem.src]?.height ?? 900)
    : 16 / 9;

  const expandedImageStyle = selectedItem
    ? {
        width: `min(calc(100vw - 2rem), calc((100vh - 2rem) * ${selectedAspectRatio}))`,
        height: `min(calc(100vh - 2rem), calc((100vw - 2rem) / ${selectedAspectRatio}))`,
      }
    : undefined;

  return (
    <>
      <section className={cn("py-12", className)}>
        <div className="relative w-full">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
            {items.map((item, index) => (
              <BlurVignette
                key={item.src}
                radius="24px"
                inset="10px"
                transitionLength="100px"
                blur="15px"
                className={cn(
                  items.length === 1
                    ? "h-96 md:col-span-5"
                    : layoutClasses[index % layoutClasses.length],
                  "rounded-[2.5rem]"
                )}
              >
                <button
                  type="button"
                  onClick={() => setSelectedItem(item)}
                  className="relative size-full text-left"
                  aria-label={`Open image: ${item.alt}`}
                >
                  <Image
                    src={item.src}
                    alt={item.alt}
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    onLoad={(event) => {
                      const target = event.currentTarget;
                      const width = target.naturalWidth;
                      const height = target.naturalHeight;

                      if (!width || !height) {
                        return;
                      }

                      setDimensionsBySrc((current) => {
                        const existing = current[item.src];

                        if (
                          existing &&
                          existing.width === width &&
                          existing.height === height
                        ) {
                          return current;
                        }

                        return {
                          ...current,
                          [item.src]: { width, height },
                        };
                      });
                    }}
                    className="rounded-[2.5rem] object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                  {item.label ? (
                    <div className="pointer-events-none absolute top-8 left-6 max-w-[calc(100%-3rem)] rounded-full bg-black/65 px-3 py-2 text-sm font-medium text-white backdrop-blur-sm md:top-9 md:left-7 md:max-w-[calc(100%-3.5rem)]">
                      {item.label}
                    </div>
                  ) : null}
                </button>
              </BlurVignette>
            ))}
          </div>
        </div>
      </section>

      <Dialog
        open={selectedItem !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedItem(null);
          }
        }}
      >
        <DialogContent className="w-fit max-w-[calc(100vw-2rem)] gap-0 border-0 bg-transparent p-0 shadow-none sm:max-w-[calc(100vw-2rem)]">
              {selectedItem ? (
                <>
                  <DialogHeader className="sr-only">
                    <DialogTitle>Expanded gallery image</DialogTitle>
                    <DialogDescription>{selectedItem.alt}</DialogDescription>
                  </DialogHeader>
                  <div
                    className="relative"
                    style={expandedImageStyle}
                  >
                    <Image
                      src={selectedItem.src}
                      alt={selectedItem.alt}
                      fill
                      sizes="100vw"
                      className="rounded-2xl object-contain"
                      priority
                    />
                    {selectedItem.label ? (
                      <div className="pointer-events-none absolute top-4 left-4 max-w-[calc(100%-2rem)] rounded-full bg-black/65 px-3 py-2 text-sm font-medium text-white backdrop-blur-sm">
                        {selectedItem.label}
                      </div>
                    ) : null}
                  </div>
                </>
              ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};

export { SegmentingExamplesGallery };

interface BlurVignetteProps {
  children: React.ReactNode;
  className?: string;
  radius?: string;
  inset?: string;
  transitionLength?: string;
  blur?: string;
}

const BlurVignette = ({
  children,
  className = "",
  radius = "24px",
  inset = "16px",
  transitionLength = "32px",
  blur = "21px",
}: BlurVignetteProps) => {
  return (
    <motion.div
      initial={{
        opacity: 0,
        scale: 0.9,
        y: -50,
      }}
      whileInView={{
        opacity: 1,
        scale: 1,
        y: 0,
      }}
      viewport={{ once: true, amount: 0.2 }}
      className={cn("group relative cursor-pointer overflow-hidden", className)}
    >
      <style>
        {`
          .blur-vignette {
            --radius: ${radius};
            --inset: ${inset};
            --transition-length: ${transitionLength};
            --blur: ${blur};
            position: absolute;
            inset: 0;
            -webkit-backdrop-filter: blur(var(--blur));
            backdrop-filter: blur(var(--blur));
            --r: max(var(--transition-length), calc(var(--radius) - var(--inset)));
            --corner-size: calc(var(--r) + var(--inset)) calc(var(--r) + var(--inset));
            --corner-gradient: transparent 0px,
              transparent calc(var(--r) - var(--transition-length)),
              black var(--r);
            --fill-gradient: black,
              black var(--inset),
              transparent calc(var(--inset) + var(--transition-length)),
              transparent calc(100% - var(--transition-length) - var(--inset)),
              black calc(100% - var(--inset));
            --fill-narrow-size: calc(100% - (var(--inset) + var(--r)) * 2);
            --fill-farther-position: calc(var(--inset) + var(--r));
            -webkit-mask-image: linear-gradient(to right, var(--fill-gradient)),
              linear-gradient(to bottom, var(--fill-gradient)),
              radial-gradient(at bottom right, var(--corner-gradient)),
              radial-gradient(at bottom left, var(--corner-gradient)),
              radial-gradient(at top left, var(--corner-gradient)),
              radial-gradient(at top right, var(--corner-gradient));
            -webkit-mask-size: 100% var(--fill-narrow-size),
              var(--fill-narrow-size) 100%,
              var(--corner-size),
              var(--corner-size),
              var(--corner-size),
              var(--corner-size);
            -webkit-mask-position: 0 var(--fill-farther-position),
              var(--fill-farther-position) 0,
              0 0,
              100% 0,
              100% 100%,
              0 100%;
            -webkit-mask-repeat: no-repeat;
            opacity: 0;
            transition: opacity 0.3s ease;
          }

          .blur-vignette.active {
            opacity: 1;
          }

          .group:hover .blur-vignette {
            opacity: 0;
          }
        `}
      </style>
      <div className="blur-vignette active" />
      {children}
    </motion.div>
  );
};
